import { createRef, IframeHTMLAttributes, RefObject, useEffect, useState } from 'react';
import styled from 'styled-components';
//@ts-ignore
import lzwCompress from 'lzwcompress';

import { ScormAssessment, ScormVersion } from 'features/scorm';
import { Loading } from 'components';

interface ScormPlayerProps extends IframeHTMLAttributes<HTMLIFrameElement> {
  iframeRef?: RefObject<HTMLIFrameElement>;
  scormVersion: ScormVersion;
  scormAssessment: ScormAssessment;
  onSaveCompletion: (completionData: string, completionPercentage: number) => void;
}

const IFRAME = styled.iframe`
  border: 0;
  flex-grow: 1;
`;

var CONSTANTS = {
  SCORM_TRUE: 'true',
  SCORM_FALSE: 'false',

  STATE_NOT_INITIALIZED: 0,
  STATE_INITIALIZED: 1,
  STATE_TERMINATED: 2,

  LOG_LEVEL_DEBUG: 1,
  LOG_LEVEL_INFO: 2,
  LOG_LEVEL_WARNING: 3,
  LOG_LEVEL_ERROR: 4,
  LOG_LEVEL_NONE: 5,

  LOG: false,
};

const ScormPlayer = ({ iframeRef: ref, scormAssessment, onSaveCompletion, scormVersion, ...props }: ScormPlayerProps) => {
  const iframeRef = ref || createRef<HTMLIFrameElement>();

  const [isInitialized, setIsInitialized] = useState(CONSTANTS.SCORM_FALSE);
  const [isFinished, setIsFinished] = useState(CONSTANTS.SCORM_FALSE);
  const [isStarted, setIsStarted] = useState(CONSTANTS.SCORM_FALSE);
  const [errorCode, setErrorCode] = useState('0');

  const [completionData, setCompletionData] = useState<Record<string, any> | null>(null);
  const [completionPercentage, setCompletionPercentage] = useState<number>(0);
  const [completionBreakpoint, setCompletionBreakpoint] = useState<Date | null>(null);

  const setInitialized = () => setIsInitialized(CONSTANTS.SCORM_TRUE);
  const setFinished = () => setIsFinished(CONSTANTS.SCORM_TRUE);

  const decompress = (stringData: any) => {
    try {
      const data = JSON.parse(stringData);

      return data.d ? JSON.parse(lzwCompress.unpack(data.d)) : data;
    } catch (e) {
      return {};
    }
  };

  useEffect(() => {
    if (!completionData) setCompletionData(scormAssessment?.completionData ? JSON.parse(scormAssessment?.completionData) : {});
  }, [scormAssessment?.completionData, completionData]);

  useEffect(() => {
    if (isStarted === CONSTANTS.SCORM_FALSE && completionData) {
      if (scormVersion === '1.2') {
        (window as any).API = {
          LMSInitialize: () => {
            if (CONSTANTS.LOG) console.log('LMSInitialize');

            if (isInitialized === CONSTANTS.SCORM_TRUE) {
              if (CONSTANTS.LOG) console.log(101, 'LMS was already initialized!');
              setErrorCode('101');
            } else if (isFinished === CONSTANTS.SCORM_TRUE) {
              if (CONSTANTS.LOG) console.log(101, 'LMS is already finished!');
              setErrorCode('101');
            } else {
              setInitialized();
              return CONSTANTS.SCORM_TRUE;
            }

            return isInitialized;
          },
          LMSFinish: () => {
            if (CONSTANTS.LOG) console.log('LMSFinish');

            if (isFinished) {
              if (CONSTANTS.LOG) console.log(102, 'LMS is already finished!');
            } else {
              setFinished();
              return CONSTANTS.SCORM_TRUE;
            }

            return isFinished;
          },
          LMSGetValue: (CMIElement: string) => {
            let response = completionData ? completionData[CMIElement] || '' : '';

            if (CONSTANTS.LOG) console.log('LMSGetValue', CMIElement, response);

            return response;
          },
          LMSSetValue: (CMIElement: string, value: any) => {
            if (CMIElement === 'cmi.core.score.raw' && value) {
              if (CONSTANTS.LOG) console.log('SetCompletionPercentageFromRaw', value);
              setCompletionPercentage(value as number);
            }

            // && !(completionData && completionData['cmi.core.score.raw'])

            if (CMIElement === 'cmi.suspend_data') {
              const data = decompress(value);

              if (data?.progress?.p) {
                if (CONSTANTS.LOG) console.log('SetCompletionPercentageFromData', value as number);
                setCompletionPercentage(data?.progress?.p as number);
              }
            }

            setCompletionData((prev) => ({ ...prev, [CMIElement]: value }));

            if (CONSTANTS.LOG) console.log('LMSSetValue', CMIElement, value);

            return CONSTANTS.SCORM_TRUE;
          },
          LMSCommit: ({ ...props } = {}) => {
            if (CONSTANTS.LOG) console.log('LMSCommit', props);

            setCompletionBreakpoint(() => new Date());

            return CONSTANTS.SCORM_TRUE;
          },
          LMSGetLastError: ({ ...props } = {}) => {
            if (CONSTANTS.LOG) console.log('LMSGetLastError', props);

            return errorCode;
          },
          LMSGetErrorString: ({ ...props } = {}) => {
            if (CONSTANTS.LOG) console.log('LMSGetErrorString', props);

            return '';
          },
          LMSGetDiagnostic: ({ ...props } = {}) => {
            if (CONSTANTS.LOG) console.log('LMSGetDiagnostic', props);

            return '';
          },
        };

        setIsStarted(CONSTANTS.SCORM_TRUE);
      }
    }
  }, [completionData, isStarted]);

  useEffect(() => {
    if (completionBreakpoint) {
      onSaveCompletion(JSON.stringify(completionData), completionPercentage);
      setCompletionBreakpoint(null);
    }
  }, [completionData, completionPercentage, completionBreakpoint]);

  return (
    <>
      {!(completionData && isStarted === CONSTANTS.SCORM_TRUE) && <Loading label="Por favor, aguarde." />}

      <IFRAME {...props} ref={iframeRef} title="SCORM" />
    </>
  );
};

export default ScormPlayer;
